import React, { useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../services/firebase';

const saveUserToFirestore = async (user, extraData = {}) => {
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        name: extraData.name || user.displayName || '',
        phone: extraData.phone || '',
        photoURL: user.photoURL || '',
        privacyAccepted: true,
        privacyAcceptedAt: new Date().toISOString(),
        bodyShape: null,
        budgetRange: null,
        preferredStyles: [],
        wishlist: [],
        searchHistory: [],
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
      });
      console.log('New user saved to Firestore');
    } else {
      await setDoc(userRef, {
        lastLogin: new Date().toISOString(),
      }, { merge: true });
      console.log('Existing user login updated');
    }
  } catch (error) {
    console.error('Error saving user:', error);
  }
};

function Login({ onClose }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);

  const handleEmailAuth = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    if (isSignUp && !name) {
      setError('Please enter your name');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (isSignUp && !privacyAccepted) {
      setPrivacyError(true);
      setError('Please accept the Privacy Policy to create an account');
      return;
    }

    setLoading(true);
    setError('');
    setPrivacyError(false);

    try {
      if (isSignUp) {
        const result = await createUserWithEmailAndPassword(
          auth, email, password
        );
        await saveUserToFirestore(result.user, { name, phone });
      } else {
        const result = await signInWithEmailAndPassword(
          auth, email, password
        );
        await saveUserToFirestore(result.user);
      }
      onClose();
    } catch (err) {
      console.error('Auth error:', err.code);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else {
        setError('Something went wrong: ' + err.code);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (isSignUp && !privacyAccepted) {
      setPrivacyError(true);
      setError('Please accept the Privacy Policy to create an account');
      return;
    }

    setLoading(true);
    setError('');
    setPrivacyError(false);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      await saveUserToFirestore(result.user);
      onClose();
    } catch (err) {
      console.error('Google login error:', err.code);
      if (err.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups and try again.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign in was cancelled. Please try again.');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('Domain not authorised. Please contact support.');
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setError('Google sign in failed: ' + err.code);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(
        `Password reset email sent to ${email}. Please check your inbox and spam folder.`
      );
    } catch (err) {
      console.error('Reset error:', err.code);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setSuccess('');
    setPrivacyError(false);
    setPrivacyAccepted(false);
    setIsForgotPassword(false);
  };

  const handleBackToLogin = () => {
    setIsForgotPassword(false);
    setError('');
    setSuccess('');
  };

  const openPrivacy = () => {
    window.open('/privacy', '_blank');
  };

  // ─── FORGOT PASSWORD SCREEN ───────────────────────────
  if (isForgotPassword) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div
          style={styles.modal}
          onClick={e => e.stopPropagation()}
        >
          <button style={styles.closeBtn} onClick={onClose}>
            X
          </button>

          <div style={styles.logo}>
            Fit<span style={styles.logoAccent}>thea</span>
          </div>

          <div style={styles.resetIcon}>🔑</div>

          <h2 style={styles.title}>Reset your password</h2>
          <p style={styles.sub}>
            Enter your email and we will send you a
            link to reset your password.
          </p>

          <input
            style={styles.input}
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
          />

          {error && (
            <div style={styles.error}>{error}</div>
          )}

          {success && (
            <div style={styles.successBox}>
              <span style={styles.successIcon}>✅</span>
              <span>{success}</span>
            </div>
          )}

          {!success && (
            <button
              style={{
                ...styles.submitBtn,
                opacity: loading ? 0.7 : 1,
              }}
              onClick={handleForgotPassword}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Email'}
            </button>
          )}

          {success && (
            <button
              style={styles.submitBtn}
              onClick={handleBackToLogin}
            >
              Back to Sign In
            </button>
          )}

          {!success && (
            <button
              style={styles.backBtn}
              onClick={handleBackToLogin}
            >
              Back to Sign In
            </button>
          )}

        </div>
      </div>
    );
  }

  // ─── MAIN LOGIN / SIGNUP SCREEN ───────────────────────
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={styles.modal}
        onClick={e => e.stopPropagation()}
      >
        <button style={styles.closeBtn} onClick={onClose}>
          X
        </button>

        <div style={styles.logo}>
          Fit<span style={styles.logoAccent}>thea</span>
        </div>

        <h2 style={styles.title}>
          {isSignUp ? 'Create your account' : 'Welcome back'}
        </h2>
        <p style={styles.sub}>
          {isSignUp
            ? 'Save your style preferences and get personalised recommendations'
            : 'Sign in to see personalised recommendations'}
        </p>

        <button
          style={styles.googleBtn}
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <span style={styles.googleIcon}>G</span>
          {isSignUp
            ? 'Sign up with Google'
            : 'Continue with Google'}
        </button>

        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine} />
        </div>

        {isSignUp && (
          <input
            style={styles.input}
            type="text"
            placeholder="Your full name"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        )}

        {isSignUp && (
          <input
            style={styles.input}
            type="tel"
            placeholder="Mobile number (optional)"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
        )}

        <input
          style={styles.input}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />

        <input
          style={styles.input}
          type="password"
          placeholder="Password (min 6 characters)"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleEmailAuth()}
        />

        {/* Forgot Password Link — only on Sign In */}
        {!isSignUp && (
          <div style={styles.forgotRow}>
            <span
              style={styles.forgotLink}
              onClick={() => {
                setIsForgotPassword(true);
                setError('');
                setSuccess('');
              }}
            >
              Forgot Password?
            </span>
          </div>
        )}

        {/* Privacy Policy Box — signup only */}
        {isSignUp && (
          <div style={{
            ...styles.privacyBox,
            borderColor: privacyError ? '#D93025' : '#EDE4DA',
            background: privacyError ? '#FDECEA' : '#FAFAFA',
          }}>
            <div style={styles.checkboxRow}>
              <input
                type="checkbox"
                id="privacy"
                checked={privacyAccepted}
                onChange={e => {
                  setPrivacyAccepted(e.target.checked);
                  if (e.target.checked) {
                    setPrivacyError(false);
                    setError('');
                  }
                }}
                style={styles.checkbox}
              />
              <span
                style={styles.checkboxText}
                onClick={() => {
                  setPrivacyAccepted(!privacyAccepted);
                  if (!privacyAccepted) {
                    setPrivacyError(false);
                    setError('');
                  }
                }}
              >
                I agree to the Privacy Policy and Terms of Service
              </span>
            </div>
            <div style={styles.privacyLinkRow}>
              <span
                style={styles.privacyLink}
                onClick={openPrivacy}
              >
                Read our Privacy Policy
              </span>
            </div>
            <p style={styles.privacyNote}>
              Fitthea uses your data only to provide
              personalised fashion recommendations.
              We never sell your data.
            </p>
          </div>
        )}

        {error && (
          <div style={styles.error}>{error}</div>
        )}

        <button
          style={{
            ...styles.submitBtn,
            opacity: loading ? 0.7 : 1,
          }}
          onClick={handleEmailAuth}
          disabled={loading}
        >
          {loading
            ? 'Please wait...'
            : isSignUp ? 'Create Account' : 'Sign In'}
        </button>

        <p style={styles.switchText}>
          {isSignUp
            ? 'Already have an account? '
            : "Don't have an account? "}
          <span
            style={styles.switchLink}
            onClick={handleSwitch}
          >
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </span>
        </p>

        {!isSignUp && (
          <p style={styles.terms}>
            By signing in you agree to our{' '}
            <span
              style={styles.termsLink}
              onClick={openPrivacy}
            >
              Privacy Policy
            </span>
            {' and Terms of Service'}
          </p>
        )}

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(44,36,32,0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    position: 'relative',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(44,36,32,0.15)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: '#8A7F7A',
    fontWeight: '600',
  },
  logo: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '26px',
    fontWeight: '700',
    color: '#2C2420',
    marginBottom: '16px',
  },
  logoAccent: {
    color: '#C8715A',
    fontStyle: 'italic',
  },
  resetIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '20px',
    fontWeight: '700',
    color: '#2C2420',
    marginBottom: '8px',
  },
  sub: {
    fontSize: '13px',
    color: '#8A7F7A',
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  googleBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    background: 'white',
    border: '1.5px solid #EDE4DA',
    borderRadius: '12px',
    padding: '13px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#2C2420',
    cursor: 'pointer',
    marginBottom: '20px',
    fontFamily: "'DM Sans', sans-serif",
  },
  googleIcon: {
    fontWeight: '700',
    fontSize: '18px',
    color: '#4285F4',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#EDE4DA',
  },
  dividerText: {
    fontSize: '12px',
    color: '#8A7F7A',
  },
  input: {
    width: '100%',
    border: '1.5px solid #EDE4DA',
    borderRadius: '12px',
    padding: '13px 16px',
    fontSize: '14px',
    fontFamily: "'DM Sans', sans-serif",
    color: '#2C2420',
    outline: 'none',
    marginBottom: '12px',
    boxSizing: 'border-box',
    background: '#FAFAFA',
  },
  forgotRow: {
    textAlign: 'right',
    marginBottom: '16px',
    marginTop: '-4px',
  },
  forgotLink: {
    fontSize: '12px',
    color: '#C8715A',
    cursor: 'pointer',
    fontWeight: '500',
    textDecoration: 'underline',
  },
  privacyBox: {
    border: '1.5px solid',
    borderRadius: '12px',
    padding: '14px',
    marginBottom: '16px',
    textAlign: 'left',
    transition: 'all 0.2s',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    accentColor: '#C8715A',
    flexShrink: 0,
  },
  checkboxText: {
    fontSize: '13px',
    color: '#2C2420',
    fontWeight: '500',
    lineHeight: '1.5',
    cursor: 'pointer',
  },
  privacyLinkRow: {
    marginBottom: '8px',
    paddingLeft: '26px',
  },
  privacyLink: {
    fontSize: '12px',
    color: '#C8715A',
    fontWeight: '600',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  privacyNote: {
    fontSize: '11px',
    color: '#8A7F7A',
    lineHeight: '1.6',
    margin: '0',
    paddingLeft: '26px',
  },
  error: {
    background: '#FDECEA',
    border: '1px solid #F5C6C2',
    borderRadius: '10px',
    padding: '10px 14px',
    fontSize: '12px',
    color: '#D93025',
    marginBottom: '12px',
    textAlign: 'left',
    lineHeight: '1.5',
  },
  successBox: {
    background: '#E8F5EE',
    border: '1px solid #C3E6CB',
    borderRadius: '10px',
    padding: '12px 14px',
    fontSize: '13px',
    color: '#2E8B57',
    marginBottom: '16px',
    textAlign: 'left',
    lineHeight: '1.6',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  successIcon: {
    flexShrink: 0,
    fontSize: '16px',
  },
  submitBtn: {
    width: '100%',
    background: '#2C2420',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    marginBottom: '16px',
    transition: 'opacity 0.2s',
  },
  backBtn: {
    width: '100%',
    background: 'none',
    border: '1.5px solid #EDE4DA',
    borderRadius: '12px',
    padding: '13px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#8A7F7A',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    marginBottom: '8px',
  },
  switchText: {
    fontSize: '13px',
    color: '#8A7F7A',
    marginBottom: '12px',
  },
  switchLink: {
    color: '#C8715A',
    cursor: 'pointer',
    fontWeight: '500',
    textDecoration: 'underline',
  },
  terms: {
    fontSize: '11px',
    color: '#B5B0A8',
    lineHeight: '1.5',
  },
  termsLink: {
    color: '#C8715A',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
};

export default Login;